/**
 * A small RFC-4180-ish CSV reader for the lead importer.
 *
 * Handles quoted fields, escaped quotes (""), and both CRLF and LF line
 * endings. Returns one object per data row keyed by the header row.
 */
/**
 * Raw grid access for callers that do their own column mapping (the import
 * dialog): every row as a string array, no header interpretation, with
 * fully-blank lines dropped.
 */
export function parseCsvRaw(text: string): string[][] {
  return splitRows(text).filter((cells) => cells.some((c) => c.trim() !== ''))
}

export function parseCsv(text: string): Record<string, string>[] {
  const rows = splitRows(text)
  if (rows.length < 2) return []

  const headers = rows[0].map((h) => h.trim().toLowerCase())

  return rows.slice(1).flatMap((cells) => {
    // Ignore blank trailing lines.
    if (cells.every((c) => c.trim() === '')) return []

    const record: Record<string, string> = {}
    headers.forEach((h, i) => {
      if (h) record[h] = (cells[i] ?? '').trim()
    })
    return [record]
  })
}

function splitRows(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let inQuotes = false

  // Strip a UTF-8 BOM so the first header isn't "﻿email".
  const input = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text

  for (let i = 0; i < input.length; i++) {
    const ch = input[i]

    if (inQuotes) {
      if (ch === '"') {
        if (input[i + 1] === '"') {
          cell += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        cell += ch
      }
      continue
    }

    if (ch === '"') {
      inQuotes = true
    } else if (ch === ',') {
      row.push(cell)
      cell = ''
    } else if (ch === '\n') {
      row.push(cell)
      rows.push(row)
      row = []
      cell = ''
    } else if (ch !== '\r') {
      cell += ch
    }
  }

  // Whatever is left after the final newline.
  if (cell !== '' || row.length) {
    row.push(cell)
    rows.push(row)
  }

  return rows
}
